/* eslint-disable react/jsx-no-bind */
import * as React from "react";
import { FC, useMemo } from "react";

import { Box, Button, Grid, Spinner, Text, Tooltip } from "@costar/theme-ui";
import { IconInfoBorder } from "@costar/theme-ui-icons/csg/IconInfoBorder";

import { CultureOption, CurrencyOption, Maybe } from "$graphql/core";

export interface SelectorGridProps {
    title: string;
    options: Maybe<readonly Maybe<CultureOption>[]> | Maybe<readonly Maybe<CurrencyOption>[]> | undefined;
    benchmarkSection?: {
        title: string;
        options: Maybe<readonly Maybe<CultureOption>[]> | undefined;
        handleOptionSelect: (optionId: Maybe<number> | undefined) => void;
    };
    tooltipText?: React.ReactElement | string;
    selectedOption?: CultureOption | CurrencyOption | null;
    handleOptionSelect: (optionId: Maybe<number> | undefined) => void;
}

const SelectorGrid: FC<SelectorGridProps> = ({
    title,
    options,
    benchmarkSection,
    tooltipText,
    selectedOption,
    handleOptionSelect,
}) => {
    const popperProps = useMemo(
        () => ({
            modifiers: {
                offset: {
                    enabled: true,
                    offset: "8,8",
                },
            },
        }),
        []
    );

    if (!options) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" paddingY="xl" height="624px">
                <Spinner size="lg" />
            </Box>
        );
    }

    return (
        <Box
            paddingBottom="xl"
            paddingTop="xl"
            maxHeight="800px"
            style={{ overflow: "auto", scrollbarGutter: "stable" }}
        >
            <Box style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "8px" }}>
                <Text fontSize="lg" fontWeight="bold">
                    {title}
                </Text>
                {tooltipText && (
                    <Tooltip
                        placement={"right-start"}
                        sx={{
                            "& p": {
                                fontSize: "16px",
                            },
                        }}
                        PopperProps={popperProps}
                        arrow
                        title={tooltipText}
                    >
                        <Button icon={<IconInfoBorder color="black" />} variant="ghost" p="0" size="md" />
                    </Tooltip>
                )}
            </Box>
            <Grid
                columns="repeat(4, 1fr)"
                gap="2px"
                paddingTop="lg"
                paddingBottom="xl"
                style={{ gridAutoRows: "1fr", alignItems: "stretch", maxWidth: "808px" }}
            >
                {options?.map((option: Maybe<CultureOption> | Maybe<CurrencyOption>) => {
                    if (option == null) return null;
                    return (
                        <Button
                            key={option.value}
                            sx={{
                                backgroundColor:
                                    selectedOption?.id === option.id ? "#004487 !important" : "transparent !important",
                                "&:hover:not(:disabled)": {
                                    backgroundColor: "#E5EEF6 !important",
                                },
                            }}
                            style={{
                                width: "100%",
                                height: "100%",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-start",
                                justifyContent: "center",
                                wordWrap: "break-word",
                                border: "none",
                                hyphens: "auto",
                                textAlign: "left",
                                padding: "8px 12px",
                            }}
                            disabled={selectedOption?.id === option.id}
                            variant="buttonDropdown"
                            onClick={() => handleOptionSelect(option.id)}
                        >
                            <Text
                                fontSize="sm"
                                style={{
                                    wordWrap: "break-word",
                                    overflowWrap: "anywhere",
                                    flex: "1",
                                    display: "flex",
                                    alignItems: "flex-end",
                                }}
                                color={selectedOption?.id === option.id ? "#FCFDFD" : "#001122"}
                            >
                                {option.name}
                            </Text>
                            <Text
                                fontSize="xs"
                                color={selectedOption?.id === option.id ? "#B2B8BD" : "#8C949C"}
                                style={{
                                    wordWrap: "break-word",
                                    overflowWrap: "anywhere",
                                    textAlign: "left",
                                    flex: "1",
                                    display: "flex",
                                    alignItems: "flex-start",
                                }}
                            >
                                {option.subtext}
                            </Text>
                        </Button>
                    );
                })}
            </Grid>

            {benchmarkSection && (
                <Box paddingTop="lg" paddingBottom="lg">
                    <Text fontSize="sm" fontWeight="bold">
                        {benchmarkSection?.title}
                    </Text>
                    <Grid
                        columns="repeat(4, 1fr)"
                        gap="2px"
                        paddingTop="24px"
                        style={{ gridAutoRows: "1fr", alignItems: "stretch", maxWidth: "808px" }}
                    >
                        {benchmarkSection?.options?.map(option => (
                            <Button
                                key={option?.value}
                                sx={{
                                    backgroundColor:
                                        selectedOption?.id === option?.id
                                            ? "#004487 !important"
                                            : "transparent !important",
                                    "&:hover:not(:disabled)": {
                                        backgroundColor: "#E5EEF6 !important",
                                    },
                                }}
                                style={{
                                    width: "100%",
                                    height: "100%",
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "flex-start",
                                    justifyContent: "center",
                                    border: "none",
                                    wordWrap: "break-word",
                                    hyphens: "auto",
                                    textAlign: "left",
                                    padding: "8px 12px",
                                }}
                                disabled={selectedOption?.id === option?.id}
                                variant="buttonDropdown"
                                onClick={() => benchmarkSection.handleOptionSelect(option?.id)}
                            >
                                <Text
                                    fontSize="sm"
                                    color={selectedOption?.id === option?.id ? "#FCFDFD" : "#001122"}
                                    style={{
                                        wordWrap: "break-word",
                                        overflowWrap: "anywhere",
                                        flex: "1",
                                        display: "flex",
                                        alignItems: "flex-end",
                                    }}
                                >
                                    {option?.name}
                                </Text>
                                <Text
                                    fontSize="xs"
                                    color={selectedOption?.id === option?.id ? "#B2B8BD" : "#8C949C"}
                                    style={{
                                        wordWrap: "break-word",
                                        overflowWrap: "anywhere",
                                        textAlign: "left",
                                        flex: "1",
                                        display: "flex",
                                        alignItems: "flex-start",
                                    }}
                                >
                                    {option?.subtext}
                                </Text>
                            </Button>
                        ))}
                    </Grid>
                </Box>
            )}
        </Box>
    );
};

export default React.memo(SelectorGrid);
